import { ApiCredentials, EncodedCredentials, parseJWT } from 'vopay';

export type AccountCredentials = {
  accountId: string;
  apiKey: string;
  secret: string;
  /** Which secret the token carries; the label follows it. */
  secretKind: 'sharedSecret' | 'signature';
};

function hasCredentialClaims(value: unknown): value is EncodedCredentials | ApiCredentials {
  return typeof value === 'object' && value !== null && 'AccountID' in value && 'ApiKey' in value;
}

function isEncodedCredentials(value: EncodedCredentials | ApiCredentials): value is EncodedCredentials {
  return 'ApiSharedSecret' in value;
}

/**
 * The account's API credentials, decoded from the JWT the listing returns as `Token` — the same
 * source, and the same shape, as the account details page. All three values come from the one token
 * on purpose: a key from anywhere else would not be the pair the secret beside it belongs to, and a
 * mismatched key/secret signs nothing. `parseJWT` throws on anything it cannot split into segments
 * and base64-decode, and a corrupt token means "no credentials to show", not a crash.
 */
export function getAccountCredentials(token: string): AccountCredentials | null {
  try {
    const claims: unknown = parseJWT(token);

    if (!hasCredentialClaims(claims)) return null;

    return {
      accountId: claims.AccountID,
      apiKey: claims.ApiKey,
      secret: isEncodedCredentials(claims) ? claims.ApiSharedSecret : claims.ApiSignature,
      secretKind: isEncodedCredentials(claims) ? 'sharedSecret' : 'signature',
    };
  } catch (error) {
    console.error(error);

    return null;
  }
}
